
import { Options, Vue } from 'vue-class-component';
import { ProviderPortalClient } from '@/services/DueDiligenceServices';
import InfoModal from '@/components/modals/infoModal.vue';
import * as DDM from '@/dueDiligenceModels';
import store from '@/store';

@Options({})
export default class ProviderSurvey extends Vue {

    template: DDM.SurveyTemplate = new DDM.SurveyTemplate();
    answers: DDM.SurveyAnswer[] = [];
    submitModel: DDM.SubmitSurveyVM = new DDM.SubmitSurveyVM();
    currentSectionIndex: number = 0;
    loaded: boolean = false;
    factorySummaries: DDM.FactorySurveySummary[] = [];
    selectedFactoryId: string = "";
    submittedResponse: DDM.SurveyResponse = null;

    yesNoPartiallyOptions = [
        { value: "yes", label: "Yes" },
        { value: "partially", label: "Partially" },
        { value: "no", label: "No" }
    ];

    yesNoOptions = [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
    ];

    get culture(): string {
        return store.state.loggedUser?.preferredCulture || "en-US";
    }

    get currentSection(): DDM.SurveySection | null {
        return this.template.sections[this.currentSectionIndex] || null;
    }

    get submitDisabled(): boolean {
        return !this.submitModel.declarationAccepted
            || !this.submitModel.declarationName.trim()
            || !this.submitModel.declarationPosition.trim();
    }

    get selectedFactorySubmitted(): boolean {
        if (!this.selectedFactoryId) return false;
        let fs = this.factorySummaries.find(f => f.factoryId === this.selectedFactoryId);
        return fs?.responseStatus === DDM.SurveyResponseStatus.Submitted;
    }

    created() {
        this.init();
    }

    init() {
        ProviderPortalClient.getSurvey(undefined)
        .then(x => {
            this.template = x.template;
            this.factorySummaries = x.factorySummaries || [];

            // Auto-select first factory if only one
            if (this.factorySummaries.length === 1) {
                this.selectedFactoryId = this.factorySummaries[0].factoryId;
                this.loadFactorySurvey();
            } else {
                this.loaded = true;
            }
        });
    }

    onFactoryChange() {
        if (!this.selectedFactoryId) {
            this.answers = [];
            this.submitModel = new DDM.SubmitSurveyVM();
            this.submittedResponse = null;
            return;
        }
        this.loadFactorySurvey();
    }

    loadFactorySurvey() {
        ProviderPortalClient.getSurvey(this.selectedFactoryId, false)
        .then(x => {
            // Update summaries
            this.factorySummaries = x.factorySummaries || this.factorySummaries;

            if (this.selectedFactorySubmitted && x.response) {
                // Submitted: store full response for readonly display
                this.submittedResponse = x.response;
                this.answers = [];
            } else if (x.response && x.response.answers) {
                this.submittedResponse = null;
                this.answers = x.response.answers;
            } else {
                this.submittedResponse = null;
                this.initializeAnswers();
            }

            this.submitModel = new DDM.SubmitSurveyVM();
            this.submitModel.factoryId = this.selectedFactoryId;
            this.currentSectionIndex = 0;
            this.loaded = true;
        });
    }

    initializeAnswers() {
        this.answers = [];
        for (let section of this.template.sections) {
            for (let question of section.questions) {
                if (question.subQuestions.length > 0) {
                    for (let sub of question.subQuestions) {
                        let answer = new DDM.SurveyAnswer();
                        answer.questionId = question.questionId;
                        answer.subQuestionId = sub.subQuestionId;
                        this.answers.push(answer);
                    }
                } else {
                    let answer = new DDM.SurveyAnswer();
                    answer.questionId = question.questionId;
                    this.answers.push(answer);
                }
            }
        }
    }

    goToSection(index: number) {
        this.currentSectionIndex = index;
        this.$nextTick(() => {
            var el = document.querySelector('.pageContainer .formContainer');
            if (el) {
                el.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });
    }

    getLocalized(ls: DDM.LocalizedString): string {
        if (!ls || !ls.values) return "";
        return ls.values[this.culture] || ls.values["en-US"] || Object.values(ls.values)[0] || "";
    }

    getAnswer(questionId: string, subQuestionId: string): DDM.SurveyAnswer {
        let found = this.answers.find(a => a.questionId === questionId && a.subQuestionId === (subQuestionId || ""));
        if (!found) {
            found = new DDM.SurveyAnswer();
            found.questionId = questionId;
            found.subQuestionId = subQuestionId || "";
            this.answers.push(found);
        }
        return found;
    }

    setAnswer(questionId: string, subQuestionId: string, value: string) {
        let answer = this.getAnswer(questionId, subQuestionId);
        answer.response = value;

        // Clear answers of conditional questions that become hidden
        this.clearHiddenConditionalAnswers(questionId);
    }

    isQuestionVisible(question: DDM.SurveyQuestion, answersSource: DDM.SurveyAnswer[]): boolean {
        if (!question || !question.conditionalOnQuestionId) return true;
        if (!answersSource || answersSource.length === 0) return false;
        return DDM.isConditionalMet(question.conditionalOnQuestionId, answersSource);
    }

    clearHiddenConditionalAnswers(triggerQuestionId: string) {
        // Find all questions that depend on this trigger
        for (let section of this.template.sections) {
            for (let question of section.questions) {
                if (question.conditionalOnQuestionId && question.conditionalOnQuestionId === triggerQuestionId && !this.isQuestionVisible(question, this.answers)) {
                    // Clear all answers for this question
                    for (let ans of this.answers) {
                        if (ans.questionId === question.questionId) {
                            ans.response = "";
                            ans.evidenceAvailable = null;
                            ans.informationalText = "";
                            ans.certificationSelections = [];
                        }
                    }
                }
            }
        }
    }

    setInformationalText(questionId: string, subQuestionId: string, value: string) {
        let answer = this.getAnswer(questionId, subQuestionId);
        answer.informationalText = value;
    }

    toggleEvidence(questionId: string, subQuestionId: string) {
        let answer = this.getAnswer(questionId, subQuestionId);
        answer.evidenceAvailable = answer.evidenceAvailable === true ? false : true;
    }

    onAnswerFileSelected(questionId: string, subQuestionId: string, ev: Event) {
        let input = ev.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;
        let file = input.files[0];
        ProviderPortalClient.uploadAnswerFile(this.selectedFactoryId, questionId, subQuestionId, file)
        .then(att => {
            this.getAnswer(questionId, subQuestionId).attachment = att;
            input.value = "";
        })
        .catch(err => {
            this.$opModal.show(InfoModal, {
                text: this.$localizationService.getLocalizedValue("Error", "Error"),
                subtitle: err.Message || err,
                deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                denyCb: () => { this.$opModal.closeLast(); }
            });
        });
    }

    setAttachmentExpiration(questionId: string, subQuestionId: string, value: string) {
        this.getAnswer(questionId, subQuestionId).attachmentExpirationDate = value || null;
    }

    removeAnswerFile(questionId: string, subQuestionId: string) {
        ProviderPortalClient.removeAnswerFile(this.selectedFactoryId, questionId, subQuestionId)
        .then(() => {
            this.getAnswer(questionId, subQuestionId).attachment = null;
            this.getAnswer(questionId, subQuestionId).attachmentExpirationDate = null;
        })
        .catch(err => {
            this.$opModal.show(InfoModal, {
                text: this.$localizationService.getLocalizedValue("Error", "Error"),
                subtitle: err.Message || err,
                deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                denyCb: () => { this.$opModal.closeLast(); }
            });
        });
    }

    getSubmittedAttachment(questionId: string, subQuestionId: string): DDM.UrlAttachment | null {
        if (!this.submittedResponse) return null;
        let answer = this.submittedResponse.answers.find(a => a.questionId === questionId && a.subQuestionId === (subQuestionId || ""));
        return answer && answer.attachment ? answer.attachment : null;
    }

    isCertificationSelected(questionId: string, certificationId: string): boolean {
        let answer = this.getAnswer(questionId, "");
        return answer.certificationSelections.some(c => c.certificationId === certificationId && c.selected);
    }

    toggleCertification(questionId: string, certificationId: string) {
        let answer = this.getAnswer(questionId, "");
        let existing = answer.certificationSelections.find(c => c.certificationId === certificationId);
        if (existing) {
            existing.selected = !existing.selected;
        } else {
            let ca = new DDM.CertificationAnswer();
            ca.certificationId = certificationId;
            ca.selected = true;
            answer.certificationSelections.push(ca);
        }
    }

    // Readonly helpers for submitted survey display
    getSubmittedValue(questionId: string, subQuestionId: string): string {
        if (!this.submittedResponse) return "";
        let answer = this.submittedResponse.answers.find(a => a.questionId === questionId && a.subQuestionId === (subQuestionId || ""));
        return answer ? answer.response : "";
    }

    getSubmittedInfo(questionId: string, subQuestionId: string): string {
        if (!this.submittedResponse) return "";
        let answer = this.submittedResponse.answers.find(a => a.questionId === questionId && a.subQuestionId === (subQuestionId || ""));
        return answer ? answer.informationalText : "";
    }

    getSubmittedEvidence(questionId: string, subQuestionId: string): boolean {
        if (!this.submittedResponse) return false;
        let answer = this.submittedResponse.answers.find(a => a.questionId === questionId && a.subQuestionId === (subQuestionId || ""));
        return answer ? answer.evidenceAvailable === true : false;
    }

    getSubmittedCert(questionId: string, certificationId: string): boolean {
        if (!this.submittedResponse) return false;
        let answer = this.submittedResponse.answers.find(a => a.questionId === questionId && a.subQuestionId === "");
        if (!answer) return false;
        return answer.certificationSelections.some(c => c.certificationId === certificationId && c.selected);
    }

    getAnswerClass(value: string): string {
        switch (value) {
            case "yes": return "answer-yes";
            case "partially": return "answer-partially";
            case "no": return "answer-no";
            default: return "answer-none";
        }
    }

    getRedFlag(flagType: DDM.RedFlagType, response: string): { label: string; cssClass: string } | null {
        return DDM.getTriggeredRedFlag(flagType, response);
    }

    saveDraft() {
        let model = new DDM.SaveSurveyDraftVM();
        model.factoryId = this.selectedFactoryId;
        model.answers = this.answers;
        ProviderPortalClient.saveSurveyDraft(model)
        .then(x => {
            this.$opModal.show(InfoModal, {
                text: this.$localizationService.getLocalizedValue("Success", "Success"),
                subtitle: this.$localizationService.getLocalizedValue("Draft saved successfully", "Draft saved successfully"),
                deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                denyCb: () => { this.$opModal.closeLast(); }
            });
        })
        .catch(err => {
            this.$opModal.show(InfoModal, {
                text: this.$localizationService.getLocalizedValue("Error", "Error"),
                subtitle: err.Message || err,
                deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                denyCb: () => { this.$opModal.closeLast(); }
            });
        });
    }

    confirmSubmit() {
        this.$opModal.show(InfoModal, {
            text: this.$localizationService.getLocalizedValue("Confirm Submission", "Confirm Submission"),
            subtitle: this.$localizationService.getLocalizedValue(
                "Once submitted, you will not be able to modify your answers. Are you sure?",
                "Once submitted, you will not be able to modify your answers. Are you sure?"
            ),
            confirm: this.$localizationService.getLocalizedValue("Submit", "Submit"),
            confirmCb: () => {
                this.$opModal.closeLast();
                this.submit();
            },
            deny: this.$localizationService.getLocalizedValue("Cancel", "Cancel"),
            denyCb: () => { this.$opModal.closeLast(); }
        });
    }

    getCertificatesMissingExpiry(): string[] {
        const missing: string[] = [];
        const find = (qid: string, sid: string) => this.answers.find(a => a.questionId === qid && a.subQuestionId === (sid || ""));
        for (const section of this.template.sections) {
            for (const q of section.questions) {
                if (q.isCertificateDocument) {
                    const a = find(q.questionId, "");
                    if (a && a.attachment && !a.attachmentExpirationDate) missing.push(this.getLocalized(q.text));
                }
                for (const sub of q.subQuestions) {
                    if (sub.isCertificateDocument) {
                        const a = find(q.questionId, sub.subQuestionId);
                        if (a && a.attachment && !a.attachmentExpirationDate) missing.push(this.getLocalized(sub.text));
                    }
                }
            }
        }
        return missing;
    }

    submit() {
        const missingExpiry = this.getCertificatesMissingExpiry();
        if (missingExpiry.length > 0) {
            this.$opModal.show(InfoModal, {
                text: this.$localizationService.getLocalizedValue("Missing expiration date", "Missing expiration date"),
                subtitle: this.$localizationService.getLocalizedValue(
                    "Please set an expiration date for the certificate documents:", "Please set an expiration date for the certificate documents:")
                    + " " + missingExpiry.join(", "),
                deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                denyCb: () => { this.$opModal.closeLast(); }
            });
            return;
        }
        this.submitModel.factoryId = this.selectedFactoryId;
        this.submitModel.answers = this.answers;
        ProviderPortalClient.submitSurvey(this.submitModel)
        .then(x => {
            // Update local summary
            let fs = this.factorySummaries.find(f => f.factoryId === this.selectedFactoryId);
            if (fs) {
                fs.responseStatus = DDM.SurveyResponseStatus.Submitted;
                fs.hasResponse = true;
            }

            // Reload the factory survey to get the full submitted response for inline display
            this.loadFactorySurvey();

            this.$opModal.show(InfoModal, {
                text: this.$localizationService.getLocalizedValue("Success", "Success"),
                subtitle: this.$localizationService.getLocalizedValue("Survey submitted successfully", "Survey submitted successfully"),
                deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                denyCb: () => {
                    this.$opModal.closeLast();
                }
            });
        })
        .catch(err => {
            this.$opModal.show(InfoModal, {
                text: this.$localizationService.getLocalizedValue("Error", "Error"),
                subtitle: err.Message || err,
                deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                denyCb: () => { this.$opModal.closeLast(); }
            });
        });
    }
}
